// @fullcalendar components
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

import momentTimezonePlugin from "@fullcalendar/moment-timezone";

// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Custom styles for Calendar
import CalendarRoot from "parts/Calendar/CalendarRoot";

// Material Dashboard 2 PRO React context
import { useMaterialUIController, LanguageContext } from "context";

import { useContext } from "react";

// Declaring props types for the Calender
interface Props {
  header?: {
    title?: string;
    date?: string;
  };
  timeZone?: string;
  [key: string]: any;
}

function Calendar({ header, ...rest }: Props): JSX.Element {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const validClassNames = [
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ];

  const events = rest.events
    ? rest.events.map((el: any) => ({
        ...el,
        className: validClassNames.find((item) => item === el.className)
          ? `event-${el.className}`
          : "event-info",
      }))
    : [];

  const { language, t } = useContext(LanguageContext) as any;

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox pt={header.title || header.date ? 2 : 0} px={2} lineHeight={1}>
        {header.title ? (
          <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
            {header.title}
          </MDTypography>
        ) : null}
        {header.date ? (
          <MDTypography component="p" variant="button" color="text" fontWeight="regular">
            {header.date}
          </MDTypography>
        ) : null}
      </MDBox>
      <MDBox
        sx={{
          "& .fc-toolbar-title": {
            whiteSpace: "nowrap",
            overflowX: "auto",
            textOverflow: "ellipsis",
            display: "inline-block",
            maxWidth: "100%",
            fontSize: "1.25rem",
            "@media (max-width: 600px)": {
              fontSize: "1rem",
            },
          },
          "& .fc-toolbar .fc-toolbar-chunk:nth-of-type(2)": {
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
          },
          "& .fc-toolbar": {
            flexWrap: "wrap",
            rowGap: "8px",
          },

          // イベントタイトルの見切れ防止スタイル
          "& .fc-event-title": {
            whiteSpace: "normal !important",
            overflow: "visible !important",
            display: "block !important",
            wordBreak: "break-word",
          },

          // イベントコンテナ（角丸・背景色付き部分）にも高さ許可
          "& .fc-event": {
            height: "auto !important",
          },
        }}
      >
        <CalendarRoot p={2} ownerState={{ darkMode }}>
          <FullCalendar
            {...rest}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, momentTimezonePlugin]}
            events={events}
            locale={language}
            timeZone={rest.timeZone || "local"}
            businessHours={true}
            height="100%"
            buttonText={{
              today: t("FullCalendar.today"),
              month: t("FullCalendar.month"),
              week: t("FullCalendar.week"),
              day: t("FullCalendar.day"),
              list: t("FullCalendar.list"),
            }}
          />
        </CalendarRoot>
      </MDBox>
    </Card>
  );
}

// Declaring default props for Calendar
Calendar.defaultProps = {
  header: {
    title: "",
    date: "",
  },
};

export default Calendar;
